// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { CredentialBilling } from '@daml.js/utility-credential-app-v0/lib/Utility/Credential/App/V0/Model/Billing';
import { useModuleParty } from '../../contexts/ModuleContext';
import { useScanProxyApiClient } from '../../contexts/ScanProxyApiContext';
import useBillCredential from '../../hooks/mutations/credential/billing/useBillCredential';
import useCancelCredentialBilling from '../../hooks/mutations/credential/billing/useCancelCredentialBilling';
import useFlushExpiredCredentialBilling from '../../hooks/mutations/credential/billing/useFlushExpiredCredentialBilling';
import useCoins from '../../hooks/queries/cn/useCoin';
import useUserService from '../../hooks/queries/credential/onboarding/useUserService';
import TemplateContract from '../../utils/TemplateContract';
import { appTransferContextWithDisclosures } from '../../utils/appTransferContextWithDisclosures';
import { ERR_CAN_BILL_CRED } from '../../utils/strings';
import { GenericAction } from '../collateral/collaterals/types';

type Row = TemplateContract<CredentialBilling>;

type ActionsCallbacks = {
  handleDistributeClick: (r: Row) => Promise<unknown> | void;
  handleTopUpClick: (r: Row) => Promise<unknown> | void;
};

export const useCreateBillingActions = ({
  handleDistributeClick,
  handleTopUpClick,
}: ActionsCallbacks) => {
  const party = useModuleParty();
  const userService = useUserService();

  const scanProxyApi = useScanProxyApiClient();
  const billCredential = useBillCredential();
  const cancelBilling = useCancelCredentialBilling();
  const flushExpiredCredentialBilling = useFlushExpiredCredentialBilling();

  const amulets = useCoins();
  const { showBoundary } = useErrorBoundary<Error>();

  const billAction = useCallback(
    async (r: Row) => {
      const { appTransferContext, disclosedContracts } = await appTransferContextWithDisclosures(
        scanProxyApi,
        30,
      );

      return billCredential({
        cid: r.contractId,
        argument: {
          appTransferContext,
          enableFeeRecord: null,
          rewardReceiver: null,
          featuredAppRightCid: null,
        },
        meta: {
          disclosedContracts,
        },
      });
    },
    [scanProxyApi, billCredential],
  );

  const cancelAction = useCallback(
    async (r: Row) => {
      if (!userService.data) {
        showBoundary(new Error(ERR_CAN_BILL_CRED, { cause: 'User service is not available.' }));
        return Promise.resolve();
      }

      const { appTransferContext, disclosedContracts } = await appTransferContextWithDisclosures(
        scanProxyApi,
        30,
      );

      return cancelBilling({
        cid: userService.data.contractId,
        argument: {
          appTransferContext,
          credentialBillingCid: r.contractId,
        },
        meta: {
          disclosedContracts,
        },
      });
    },
    [userService.data, scanProxyApi, cancelBilling, showBoundary],
  );

  const flushExpiredDepositAction = useCallback(
    async (r: Row) => {
      return flushExpiredCredentialBilling({
        cid: r.contractId,
        argument: { actor: party },
      });
    },
    [flushExpiredCredentialBilling, party],
  );

  const createActions = (r: Row) => {
    const actions: GenericAction<Row>[] = [];

    if (r.payload.operator === party) {
      actions.push({
        name: 'Bill',
        action: billAction,
        inMoreMenu: false,
      });

      actions.push({
        name: 'Flush',
        action: flushExpiredDepositAction,
        buttonProps: {
          variant: 'outlined',
          color: 'warning',
        },
        inMoreMenu: false,
      });
    }

    if (r.payload.issuer === party || r.payload.holder === party) {
      actions.push({ name: 'Cancel', action: cancelAction, inMoreMenu: true });
    }

    if (r.payload.issuer === party) {
      actions.push({
        name: 'Distribute',
        action: handleDistributeClick,
        inMoreMenu: true,
      });
    }

    const areAmuletsAvailable = amulets?.data?.length !== 0;

    if (r.payload.holder === party) {
      actions.push({
        name: areAmuletsAvailable ? 'Top Up' : 'Balance Too Low',
        action: handleTopUpClick,
        disabled: !areAmuletsAvailable,
        inMoreMenu: true,
      });
    }

    return actions;
  };

  return { createActions };
};
